import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  PaymentRequestButtonElement,
  useStripe,
} from "@stripe/react-stripe-js";
import cn from "classnames";
import { PaymentRequest } from "@stripe/stripe-js";

import styles from "./style.module.scss";
import {
  ReducedOrder,
  CustomerPaymentInitiateResponse,
} from "../../generated/graphql";
import { useCustomerCancelPayment } from "../../useCases/customerPayment";
import { ToastContext } from "../../contexts/ToastContext";
import { AddressFieldsState, NewAddressFieldsState } from "../../types";

interface DigitalWalletButtonProps {
  order: ReducedOrder;
  stripePaymentId: string;
  totalPrice: number;
  isDisabled: boolean;
  isLoading: boolean;
  setSupportDigitalWallet: (value: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  onSuccess: () => void;
  initiatePayment: (
    billing?: AddressFieldsState
  ) => Promise<CustomerPaymentInitiateResponse | null>;
  paymentQueryPolling: (paymentId: string) => Promise<boolean>;
}

const DigitalWalletButton: React.FC<DigitalWalletButtonProps> = (
  props: DigitalWalletButtonProps
) => {
  const {
    totalPrice,
    isDisabled,
    setIsLoading,
    isLoading,
    onSuccess,
    initiatePayment,
    paymentQueryPolling,
    setSupportDigitalWallet,
  } = props;

  const { presentToast } = useContext(ToastContext);
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
    null
  );

  const cancelPayment = useCustomerCancelPayment();

  useEffect(() => {
    if (stripe) {
      if (paymentRequest === null) {
        const pr = stripe.paymentRequest({
          // TODO: Support other currencies
          country: "HK",
          currency: "hkd",
          total: {
            label: "Total amount",
            amount: Math.trunc(totalPrice * 100),
          },
          requestPayerName: true,
        });
        // Check the availability of the Payment Request API.
        pr.canMakePayment()
          .then(result => {
            if (!result) return;
            setSupportDigitalWallet(true);
            setPaymentRequest(pr);
          })
          .catch(() => {
            console.error("Fail initiate payment request");
          });
      }
    }
  }, [paymentRequest, totalPrice, setSupportDigitalWallet, stripe]);

  const onclickButton = useCallback(() => {
    if (isLoading) {
      return;
    }

    if (stripe) {
      if (paymentRequest === null) {
        return;
      }

      paymentRequest.on("paymentmethod", async ev => {
        setIsLoading(true);
        try {
          const billingAddress = ev.paymentMethod.billing_details.address;
          const billing: NewAddressFieldsState = {
            type: "new",
            address1: billingAddress?.line1 ?? "",
            address2: billingAddress?.line2 ?? "",
            country: billingAddress?.country ?? "",
            saveForFutureUse: false,
          };
          const intent = await initiatePayment(billing);
          if (!intent) {
            throw new Error("initiate-payment-failed");
          }
          // Confirm the PaymentIntent without handling potential next actions (yet).
          /* eslint-disable @typescript-eslint/camelcase */
          const confirmResult = await stripe.confirmCardPayment(
            intent.token,
            { payment_method: ev.paymentMethod.id },
            { handleActions: false }
          );
          /* eslint-enable @typescript-eslint/camelcase */
          if (confirmResult.error) {
            throw new Error("payment-failed");
          }
          // Let Stripe.js handle the rest of the payment flow.
          const processResult = await stripe.confirmCardPayment(intent.token);
          if (processResult.error) {
            // The payment failed -- ask your customer for a new payment method.
            throw new Error("payment-failed");
          }
          // The payment has succeeded.
          const paymentComplete = await paymentQueryPolling(intent.id);
          if (!paymentComplete) {
            await cancelPayment(intent.id);
            throw new Error("payment-not-captured");
          }
          // Report to the browser that the confirmation was successful, prompting
          // it to close the browser payment method collection interface.
          ev.complete("success");

          onSuccess();
        } catch (e) {
          console.error(e);
          // Report to the browser that the payment failed, prompting it to
          // re-show the payment interface, or show an error message and close
          // the payment interface.
          ev.complete("fail");
          presentToast({ content: "screen.paymentInfoScreen.fail" }, true);
        } finally {
          setIsLoading(false);
        }
      });
    }
  }, [
    stripe,
    initiatePayment,
    presentToast,
    setIsLoading,
    onSuccess,
    cancelPayment,
    paymentQueryPolling,
    paymentRequest,
    isLoading,
  ]);

  return (
    <div
      className={cn(styles.digitalWalletContainer, {
        [styles.disabled]: isDisabled || isLoading,
      })}
    >
      {paymentRequest && (
        <PaymentRequestButtonElement
          onClick={onclickButton}
          options={{ paymentRequest }}
        />
      )}
    </div>
  );
};

export default DigitalWalletButton;
